import { NgModel } from '@angular/forms';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { DesktopUserCredentialModel } from '@fi-app/core/desktop-user-credential.model';
import { AppTextService } from '@fi-shared/app-text';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { AuthenticateService } from '@fi-app/utilities/fi-services/authenticate.service';
import { SessionService } from '@fi-app/utilities/fi-services/session.service';
import { FiBrowserCheckService } from '@fi-app/utilities/notification/browser-check/browser-check.service';
import { FiAuthenticateModuleService } from '../authenticate-module.service';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { MagicLinkUpdatePasswordDialogComponent } from '../magic-link-update-password-dialog/magic-link-update-password-dialog.component';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { INotificationItem } from '@fi-app/utilities/notification/notification';
import { UpgradeMessageNotificationService } from '@fi-app/utilities/notification/upgrade-message-notification/upgrade-message-notification.service';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { IBackendErrorData } from '@fi-shared/fi-backend/fi-backend.service';
import { FiAuthenticateHelperService } from '@fi-shared/fi-auth-shared/fi-authenticate-helper.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { ILoginSettings } from '@fi-shared/fi-auth-shared/fi-auth-shared';
import { TResultPromise, TResultPromiseData } from '@fi-shared/fi-shared-interfaces';
import { FiDownloadAppDialogComponent } from '@fi-app/authenticate/login/download-app-dialog/fi-download-app-dialog.component';

@Component({
    selector: 'fi-authenticate-login',
    templateUrl: './login.component.html',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['../authenticate.scss', './login.component.scss'],
    host: { class: 'fi-authenticate-login' },
    standalone: false,
})
export class FiAuthenticateLoginComponent implements OnInit, OnDestroy, AfterViewInit {
    @Input() checkAuthenticated: boolean = true;
    @Input() magicLink: string;
    @Input() autoLogin: string;
    @Input() skipCheckNotifications: boolean = false;
    userCredential = new DesktopUserCredentialModel();
    settings: ILoginSettings = {
        RememberMe: false,
    };
    ShowStayLoggedIn: boolean;
    notifications: INotificationItem[] = [];
    isOngoingUpgrade: boolean;
    selectedAdConfig;
    now = Date.now();

    FiStateNames = FiStateNames;

    @ViewChild('fiLoginCredentialsModel', { static: false }) fiLoginCredentialsModel: NgModel;

    constructor(
        private fiDialogsService: FiDialogsService,
        private appTextService: AppTextService,
        private loadingService: LoadingService,
        private authService: AuthenticateService,
        private sessionService: SessionService,
        private browserCheckService: FiBrowserCheckService,
        private upgradeMessageNotificationService: UpgradeMessageNotificationService,
        private authModuleService: FiAuthenticateModuleService,
        private authenticateHelper: FiAuthenticateHelperService,
        private stateService: FiStateService,
        private userDataService: FiUserDataService,
        private customerDataService: FiCustomerDataService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit() {
        this.userCredential.Login = this.userDataService.getLoginRememberMe();
        this.userCredential.Password = undefined;
        this.settings.RememberMe = !!this.userDataService.getLoginRememberMe();
        this.userCredential.ShouldStayLoggedIn = this.ShowStayLoggedIn =
            this.customerDataService.customerData.ShowStayLoggedIn;

        this.authService.debugApi2MagicLink();
        if (this.magicLink) {
            this.authService.isMagicLinkValid(this.magicLink).then(
                () => {
                    this.fiDialogsService
                        .custom({
                            headerText: this.appTextService.get('auth.logging_in'),
                            yesButtonText: this.appTextService.get('auth.save_password'),
                            noButtonText: this.appTextService.get('common.cancel'),
                            contentComponentRef: MagicLinkUpdatePasswordDialogComponent,
                            config: {
                                minWidth: 'unset',
                            },
                        })
                        .onYesButtonClick((newPassword) => {
                            this.authService.updatePassword(this.magicLink, newPassword).then((userName) => {
                                this.userCredential.Login = userName;
                                this.userCredential.Password = newPassword;
                                //if (!this.userDataService.userData) {
                                this.Login();
                                //}
                            });
                        });
                },
                (err: IBackendErrorData) => {
                    this.authenticateHelper.handleInvalidMagicLink(err, {
                        requestNewMagicLinkFn: () => this.authService.requestNewMagicLink(this.magicLink),
                    });
                },
            );
        } else if (this.autoLogin) {
            this.fiDialogsService.notify({
                messageText: this.appTextService.get('auth.auto_login_not_supported'),
            });
        } else if (this.checkAuthenticated) {
            //Extra check in case user has cached tokens and it doesn't exist in the database.
            this.authService.IsTokenValid(this.userDataService.getToken()).then((isValid) => {
                if (isValid) {
                    this.stateService.go(FiStateNames.Home._name);
                } else {
                    this.sessionService.resetSession();
                }
            });
        }

        if (!this.skipCheckNotifications) {
            this.notifications.push(this.browserCheckService.checkCurrentBrowser());
            this.notifications.push(this.upgradeMessageNotificationService.checkUpgradeMessageNotification());
            this.isOngoingUpgrade = this.upgradeMessageNotificationService.isOngoingUpgrade();
        }

        if (this.isADLogin()) {
            const configs = this.getADConfigs();
            if (configs.length === 1) {
                this.selectedAdConfig = configs[0];
            }
        }

        this.showDownloadDialogOnMobile();
    }

    private showDownloadDialogOnMobile(): TResultPromise {
        // should return { result: true } if user chose 'download app'
        // in this case we should not process in web magicLink, autoLogin params and also check if token valid
        const promise = new Promise<TResultPromiseData>((resolve) => {
            if (!this.browserCheckService.isMobile()) {
                resolve({ result: false });
                return;
            } else {
                const url = this.getOpenMobileUrl();

                this.fiDialogsService
                    .custom({
                        contentComponentRef: FiDownloadAppDialogComponent,
                        contentComponentData: url,
                        headerText: this.appTextService.get('common.download_the_app'),
                        headerIconClass: 'far fa-circle-info BrandedColor',
                        isHeaderDividerShown: true,
                        hideDialogTopBorder: true,
                        hideFooter: true,
                        headerDividerClass: 'BrandedBorderColor',
                        preventUppercaseButtonLabel: true,
                        config: {
                            panelClass: ['fi-rounded-dialog-panel', 'fi-download-app-dialog-panel'],
                            autoFocus: false,
                            width: '320px',
                        },
                    })
                    .onYesButtonClick(() => {
                        resolve({ result: true });
                    })
                    .onNoButtonClick(() => {
                        resolve({ result: false });
                    });
            }
        });
        return promise;

        //     this.fiDialogsService.notify({
        //         headerText: this.appTextService.get('common.download_the_app'),
        //         headerIconClass: 'far fa-circle-info BrandedColor',
        //         isHeaderDividerShown: true,
        //         hideDialogTopBorder: true,
        //         headerDividerClass: 'BrandedBorderColor',
        //         messageText: this.appTextService.get('download_app.dialog_content'),
        //         yesButtonText: this.appTextService.get('common.download_app'),
        //         noButtonText: this.appTextService.get('common.close'),
        //         preventUppercaseButtonLabel: true,
        //         config: {
        //             panelClass: ['fi-rounded-dialog-panel', 'fi-download-app-dialog-panel'],
        //             autoFocus: false,
        //             width: '320px'
        //         }
        //     }).onYesButtonClick(() => {
        //         // this.stateService.go(FiStateNames.LoginV2._name, {autoLogin: true});
        //         window.localStorage.setItem(this.mobileAppLoginStorageKey, 'true');
        //         const link = document.createElement('a');
        //         const path = isDefined(this.autoLogin) ? `/Login?autoLogin=${this.autoLogin}` : '/Login';
        //         link.href = window.location.origin + path;
        //         document.body.appendChild(link);
        //         link.click();
        //         document.body.removeChild(link);
        //     });
    }

    ngAfterViewInit(): void {
        this.cdr.detectChanges();
    }

    ngOnDestroy() {
        this.notifications
            .filter((notification) => notification)
            .forEach((notification) => notification.HideNotification());
    }

    Login() {
        if (this.isADLogin()) {
            this.processADLogin();
            return;
        }

        this.userCredential.Login = this.authenticateHelper.handleUserLoginNumber(this.userCredential.Login);

        this.authModuleService.LoginWithCredentials(this.userCredential.toLoginData(this.settings));
    }

    AddAsBookmark() {
        if (navigator.userAgent.match(/(iPhone|iPod|iPad|Android|BlackBerry|IEMobile)/)) {
        } else {
            this.fiDialogsService.notify({
                messageText: this.appTextService.get('common.add_bookmark'),
            });
        }
    }

    IsRequestAccessAvailable() {
        return this.customerDataService.customerData.IsRequestAccessAvailable;
    }

    isADLogin() {
        return this.customerDataService.isADLogin();
    }

    getADConfigs() {
        return this.customerDataService.customerData.AdConfigs;
    }

    processADLogin() {
        if (!this.selectedAdConfig) {
            this.fiDialogsService.error({
                messageText: this.appTextService.get('ad_login.credentials_error'),
            });
        } else {
            this.loadingService.startLoading();
            this.authModuleService.loginWithAd({ configId: this.selectedAdConfig.Id });
        }
    }

    // note: not used
    onOngoingUpgradeMessageChanged($event) {
        this.isOngoingUpgrade = $event;
    }

    onValidateError(inputsWithErrors: NgModel[]) {
        if (inputsWithErrors.find((input) => input.name === 'userName' || input.name === 'password'))
            this.fiDialogsService.error({
                headerText: this.appTextService.get('error.missing_sign_in_credentials_header'),
                messageText: this.appTextService.get('error.missing_sign_in_credentials_message'),
            });
    }

    private getOpenMobileUrl(): string {
        let currentUrl = window.location.href;
        const url = `https://mobile.chainsystems.net/launch?from=${currentUrl}`;
        //const url = `https://local.chainformation.com/launch?from=${currentUrl}`;
        return url;
    }
}
